import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';

import { AppConfigService } from 'app/core/config/app-config.service';

export interface MessageCountersResponse {
  totals: {
    messages_unread: number;
    notifications_needs_action: number;
    waiting_for_student: number;
  };
  by_student: Record<string, {
    messages_unread: number;
    notifications_needs_action: number;
    waiting_for_student: number;
  }>;
}

/**
 * Shared /v1/messages/counters client with in-flight dedupe.
 * Kept separate from SupervisorService to avoid a DI cycle:
 * SupervisorService -> StudentCourseService -> NotificationsService.
 */
@Injectable({
  providedIn: 'root',
})
export class MessageCountersService {
  private _inflight$: Observable<MessageCountersResponse> | null = null;
  private _cache$ = new BehaviorSubject<MessageCountersResponse | null>(null);

  constructor(
    private httpClient: HttpClient,
    private appConfig: AppConfigService,
  ) {}

  getCounters(): Observable<MessageCountersResponse> {
    if (this._inflight$) {
      return this._inflight$;
    }

    const apiUrl = `${this.appConfig.get<string>('serverUrl', '')}/v1/messages/counters`;

    this._inflight$ = this.httpClient.get<any>(apiUrl, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
      }),
    }).pipe(
      map((response: any) => {
        const payload = response?.data || {};
        return {
          totals: {
            messages_unread: Number(payload?.totals?.messages_unread || 0),
            notifications_needs_action: Number(payload?.totals?.notifications_needs_action || 0),
            waiting_for_student: Number(payload?.totals?.waiting_for_student || 0),
          },
          by_student: payload?.by_student || {},
        } as MessageCountersResponse;
      }),
      tap((parsed) => this._cache$.next(parsed)),
      catchError(() => of({
        totals: {
          messages_unread: 0,
          notifications_needs_action: 0,
          waiting_for_student: 0,
        },
        by_student: {},
      } as MessageCountersResponse)),
      finalize(() => {
        this._inflight$ = null;
      }),
      shareReplay(1),
    );

    return this._inflight$;
  }

  getLatest(): MessageCountersResponse | null {
    return this._cache$.getValue();
  }
}
